import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export interface CookieConsentSettings {
  necessary: boolean; // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  consentDate?: Date;
  consentVersion: string;
}

interface CookieConsentContextType {
  settings: CookieConsentSettings;
  hasConsent: boolean;
  showBanner: boolean;
  updateConsent: (newSettings: Partial<CookieConsentSettings>) => void;
  acceptAll: () => void;
  acceptNecessary: () => void;
  openSettings: () => void;
  closeBanner: () => void;
  revokeConsent: () => void;
}

const CONSENT_VERSION = '2.0'; // Updated July 2025
const CONSENT_STORAGE_KEY = 'cookie-consent-v1';
const SHOULD_LOG_COOKIE_DEBUG = process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true';

const defaultSettings: CookieConsentSettings = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
  consentVersion: CONSENT_VERSION,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<CookieConsentSettings>(() => {
    try {
      const savedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (savedConsent) {
        const parsed = JSON.parse(savedConsent);
        // Check if consent version matches
        if (parsed.consentVersion === CONSENT_VERSION) {
          return {
            ...parsed,
            consentDate: parsed.consentDate ? new Date(parsed.consentDate) : undefined,
          };
        }
      }
    } catch (error) {
      console.warn('Failed to parse cookie consent from localStorage:', error);
    }
    return defaultSettings;
  });

  const [showBanner, setShowBanner] = useState(() => {
    // Show banner if no consent has been given or version mismatch
    try {
      const savedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!savedConsent) return true;
      
      const parsed = JSON.parse(savedConsent);
      return parsed.consentVersion !== CONSENT_VERSION;
    } catch {
      return true;
    }
  });

  const hasConsent = settings.consentDate !== undefined;

  const saveSettings = useCallback((newSettings: CookieConsentSettings) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newSettings));
    } catch (error) {
      console.warn('Failed to save cookie consent to localStorage:', error);
    }
  }, []);

  // Apply consent settings to third-party services
  const applyConsentSettings = useCallback((consentSettings: CookieConsentSettings) => {
    // Google Analytics - Only load if analytics consent is given
    if (consentSettings.analytics && typeof window !== 'undefined') {
      // Enable Google Analytics if consent is given
      (window as any).gtag?.('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: consentSettings.marketing ? 'granted' : 'denied',
        ad_user_data: consentSettings.marketing ? 'granted' : 'denied',
        ad_personalization: consentSettings.marketing ? 'granted' : 'denied',
      });
      
      // Load Google Analytics script if not already loaded
      if (!document.querySelector('script[src*="googletagmanager.com/gtag"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID';
        document.head.appendChild(script);
        
        const configScript = document.createElement('script');
        configScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'GA_MEASUREMENT_ID', {
            anonymize_ip: true,
            cookie_flags: 'secure;samesite=strict',
          });
        `;
        document.head.appendChild(configScript);
      }
    } else if (typeof window !== 'undefined') {
      // Disable Google Analytics and revoke consent
      (window as any).gtag?.('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }

    // reCAPTCHA - functional cookies - Essential for contact form security
    if (!consentSettings.functional && SHOULD_LOG_COOKIE_DEBUG) {
      console.info('GDPR Notice: reCAPTCHA requires functional cookies for contact form security. Contact form will be disabled until consent is granted.');
    }
    
    // Log consent decision for audit trail (anonymized)
    if (SHOULD_LOG_COOKIE_DEBUG) {
      console.info('Cookie consent updated:', {
        version: consentSettings.consentVersion,
        date: consentSettings.consentDate,
        analytics: consentSettings.analytics,
        marketing: consentSettings.marketing,
        functional: consentSettings.functional
      });
    }
  }, []);

  const clearTrackingData = useCallback(() => {
    // Clear Google Analytics data
    if (typeof window !== 'undefined') {
      // Clear gtag data
      (window as any).gtag?.('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
      });
      
      // Clear Google Analytics cookies
      const gaCookies = document.cookie.split(';').filter(cookie => 
        cookie.trim().startsWith('_ga') || 
        cookie.trim().startsWith('_gid') ||
        cookie.trim().startsWith('_gat')
      );
      
      gaCookies.forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim();
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });
    }
  }, []);

  const updateConsent = useCallback((newSettings: Partial<CookieConsentSettings>) => {
    const updatedSettings: CookieConsentSettings = {
      ...settings,
      ...newSettings,
      necessary: true, // Always true
      consentDate: new Date(),
      consentVersion: CONSENT_VERSION,
    };
    
    setSettings(updatedSettings);
    saveSettings(updatedSettings);
    setShowBanner(false);
    
    // Apply consent settings
    applyConsentSettings(updatedSettings);
  }, [settings, saveSettings, applyConsentSettings]);

  const acceptAll = useCallback(() => {
    updateConsent({
      analytics: true,
      marketing: true,
      functional: true,
    });
  }, [updateConsent]);

  const acceptNecessary = useCallback(() => {
    updateConsent({
      analytics: false,
      marketing: false,
      functional: false,
    });
  }, [updateConsent]);

  const openSettings = useCallback(() => {
    setShowBanner(true);
  }, []);

  const closeBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  const revokeConsent = useCallback(() => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      localStorage.removeItem('accessibility-settings'); // Clear other stored data
    } catch (error) {
      console.warn('Failed to revoke consent:', error);
    }
    
    setSettings(defaultSettings);
    setShowBanner(true);
    
    // Clear any tracking data
    clearTrackingData();
  }, [clearTrackingData]);

  // Apply settings on mount if consent exists
  useEffect(() => {
    if (hasConsent) {
      applyConsentSettings(settings);
    }
  }, [hasConsent, settings, applyConsentSettings]);

  const contextValue = {
    settings,
    hasConsent,
    showBanner,
    updateConsent,
    acceptAll,
    acceptNecessary,
    openSettings,
    closeBanner,
    revokeConsent,
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
};
